/**
 * Glisser-déposer des rubriques et canevas entre le panneau gauche, l'édition, le sommaire et la discussion
 */

// Type propre à l'application : ni les champs texte ni TinyMCE n'insèrent ce contenu au dépôt
export const RUBRIC_DRAG_TYPE = 'application/x-now-rubric';

export const isRubricDrag = (event) =>
  Array.from(event.dataTransfer?.types || []).includes(RUBRIC_DRAG_TYPE);

/**
 * Emplacement d'insertion sous le pointeur, parmi les descendants de container
 * marqués data-rubric-slot="<id de rubrique>" (dans l'ordre du document).
 * @returns {{ index: number, beforeRubricId: string|null }} beforeRubricId null = en fin de liste
 */
export const getRubricDropTarget = (container, clientY) => {
  const slots = container ? Array.from(container.querySelectorAll('[data-rubric-slot]')) : [];
  const index = slots.findIndex((slot) => {
    const rect = slot.getBoundingClientRect();
    return clientY < rect.top + rect.height / 2;
  });
  return index === -1
    ? { index: slots.length, beforeRubricId: null }
    : { index, beforeRubricId: slots[index].dataset.rubricSlot };
};

/**
 * Déposer une rubrique juste avant elle-même ou juste après ne change rien à l'ordre
 */
export const isRubricMoveNoop = (container, rubricId, index) => {
  const slots = container ? Array.from(container.querySelectorAll('[data-rubric-slot]')) : [];
  const from = slots.findIndex((slot) => slot.dataset.rubricSlot === rubricId);
  return from !== -1 && (index === from || index === from + 1);
};

/**
 * Insère newRubrics avant la rubrique beforeRubricId (en fin de liste si null ou introuvable)
 * et renumérote les positions.
 */
export const insertRubricsBefore = (rubrics, newRubrics, beforeRubricId = null) => {
  const sorted = [...rubrics].sort((a, b) => a.position - b.position);
  const found = beforeRubricId ? sorted.findIndex((rubric) => rubric.id === beforeRubricId) : -1;
  const index = found === -1 ? sorted.length : found;
  return [...sorted.slice(0, index), ...newRubrics, ...sorted.slice(index)]
    .map((rubric, i) => ({ ...rubric, position: i + 1 }));
};

/**
 * Déplace la rubrique rubricId avant beforeRubricId (en fin de liste si null ou introuvable)
 * et renumérote les positions.
 * @returns {Array|null} null si la rubrique est introuvable ou si l'ordre ne change pas
 */
export const reorderRubricBefore = (rubrics, rubricId, beforeRubricId = null) => {
  const sorted = [...rubrics].sort((a, b) => a.position - b.position);
  const moving = sorted.find((rubric) => rubric.id === rubricId);
  if (!moving || rubricId === beforeRubricId) return null;

  const others = sorted.filter((rubric) => rubric.id !== rubricId);
  const found = beforeRubricId ? others.findIndex((rubric) => rubric.id === beforeRubricId) : -1;
  const index = found === -1 ? others.length : found;
  const reordered = [...others.slice(0, index), moving, ...others.slice(index)];
  if (reordered.every((rubric, i) => rubric.id === sorted[i].id)) return null;

  return reordered.map((rubric, i) => ({ ...rubric, position: i + 1 }));
};

const describeDragItem = (item) => {
  if (item.kind === 'standard') {
    const count = item.count || 0;
    return `Canevas · ${count} rubrique${count > 1 ? 's' : ''}`;
  }
  return item.kind === 'reportRubric' ? 'Rubrique du document' : 'Rubrique';
};

/**
 * Vignette suivant le pointeur pendant le glissement, à la place d'une capture de l'élément source
 * (une poignée seule, ou une ligne de liste coupée par le bord du panneau)
 */
export const setRubricDragImage = (event, item) => {
  if (!event.dataTransfer?.setDragImage) return;

  const ghost = document.createElement('div');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    maxWidth: '280px',
    padding: '6px 12px',
    borderRadius: '8px',
    backgroundColor: '#1976d2',
    color: '#ffffff',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    pointerEvents: 'none'
  });

  const title = document.createElement('div');
  title.textContent = item.label;
  Object.assign(title.style, {
    fontSize: '13px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  });

  const detail = document.createElement('div');
  detail.textContent = describeDragItem(item);
  Object.assign(detail.style, { fontSize: '11px', opacity: '0.85' });

  ghost.append(title, detail);
  document.body.appendChild(ghost);
  event.dataTransfer.setDragImage(ghost, 16, 16);
  // Le navigateur capture la vignette pendant dragstart
  setTimeout(() => ghost.remove(), 0);
};
